import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Building2, User, Target, Upload, CheckCircle, ChevronRight, 
  ChevronLeft, Image, Trash2, Globe, Mail, Phone, Briefcase,
  TrendingUp, DollarSign, Users, Award, FileText, SkipForward
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StepIndicator = ({ currentStep, totalSteps, steps }) => (
  <div className="mb-8">
    <div className="flex items-center justify-between mb-2">
      {steps.map((step, index) => (
        <div key={index} className="flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
            index < currentStep ? 'bg-primary border-primary text-primary-foreground' :
            index === currentStep ? 'border-primary text-primary' :
            'border-muted text-muted-foreground'
          }`}>
            {index < currentStep ? <CheckCircle className="w-5 h-5" /> : index + 1}
          </div>
          {index < steps.length - 1 && (
            <div className={`w-24 h-0.5 mx-2 ${index < currentStep ? 'bg-primary' : 'bg-muted'}`} />
          )}
        </div>
      ))}
    </div>
    <div className="flex justify-between">
      {steps.map((step, index) => (
        <span key={index} className={`text-xs ${index <= currentStep ? 'text-foreground' : 'text-muted-foreground'}`}>
          {step}
        </span>
      ))}
    </div>
  </div>
);

const LogoUploader = ({ logoType, label, description, currentLogo, onUpload, onDelete }) => {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Use PNG, JPEG, SVG, or WebP');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum 5MB');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result.split(',')[1];
        await onUpload(logoType, base64, file.name, file.type);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload logo');
      setUploading(false);
    }
  };

  return (
    <div className="border border-dashed border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
      <div className="flex items-start gap-4">
        <div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center overflow-hidden">
          {currentLogo ? (
            <img src={`${BACKEND_URL}${currentLogo.url}`} alt={label} className="w-full h-full object-contain" />
          ) : (
            <Image className="w-8 h-8 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-sm mb-1">{label}</h4>
          <p className="text-xs text-muted-foreground mb-3">{description}</p>
          <div className="flex gap-2">
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={handleFileChange}
                data-testid={`upload-${logoType}`}
              />
              <Button size="sm" variant="outline" disabled={uploading} asChild>
                <span>
                  <Upload className="w-3 h-3 mr-1" />
                  {uploading ? 'Uploading...' : 'Upload'}
                </span>
              </Button>
            </label>
            {currentLogo && (
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(logoType)}>
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const Onboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState({});
  const [logos, setLogos] = useState([]);
  const [onboardingStatus, setOnboardingStatus] = useState(null);

  // Form states
  const [brief, setBrief] = useState({
    company_name: '',
    industry: '',
    company_size: '',
    website: '',
    primary_contact_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
    current_platforms: [],
    monthly_ad_spend: '',
    target_markets: [],
    target_audience: '',
    primary_goals: [],
    main_challenges: [],
    key_competitors: [],
    additional_notes: ''
  });

  const [kpi, setKpi] = useState({
    primary_kpis: [],
    target_cpa: '',
    target_roas: '',
    target_ctr: '',
    monthly_conversion_target: '',
    brand_awareness_goal: '',
    campaign_priorities: [],
    budget_allocation_preference: '',
    reporting_frequency: '',
    additional_metrics: ''
  });

  const steps = ['Company Info', 'Goals & Challenges', 'KPIs & Targets', 'Brand Assets'];

  const fetchData = useCallback(async () => {
    try {
      const [optionsRes, statusRes, briefRes, kpiRes, logosRes] = await Promise.all([
        axios.get(`${API}/onboarding/options`, { withCredentials: true }),
        axios.get(`${API}/onboarding/status`, { withCredentials: true }),
        axios.get(`${API}/onboarding/brief`, { withCredentials: true }),
        axios.get(`${API}/onboarding/kpi`, { withCredentials: true }),
        axios.get(`${API}/onboarding/logos`, { withCredentials: true })
      ]);

      setOptions(optionsRes.data);
      setOnboardingStatus(statusRes.data);
      setLogos(logosRes.data.logos || []);

      if (briefRes.data.brief) {
        setBrief(prev => ({ ...prev, ...briefRes.data.brief }));
      }
      if (kpiRes.data.feedback) {
        setKpi(prev => ({ ...prev, ...kpiRes.data.feedback }));
      }
    } catch (error) {
      console.error('Error fetching onboarding data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveBrief = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/onboarding/brief`, brief, { withCredentials: true });
      toast.success('Company information saved');
    } catch (error) {
      toast.error('Failed to save company information');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveKPI = async () => {
    setSaving(true);
    try {
      const kpiData = {
        ...kpi,
        target_cpa: kpi.target_cpa ? parseFloat(kpi.target_cpa) : null,
        target_roas: kpi.target_roas ? parseFloat(kpi.target_roas) : null,
        target_ctr: kpi.target_ctr ? parseFloat(kpi.target_ctr) : null,
        monthly_conversion_target: kpi.monthly_conversion_target ? parseInt(kpi.monthly_conversion_target) : null
      };
      await axios.post(`${API}/onboarding/kpi`, kpiData, { withCredentials: true });
      toast.success('KPI targets saved');
    } catch (error) {
      toast.error('Failed to save KPI targets');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadLogo = async (logoType, fileData, fileName, contentType) => {
    try {
      await axios.post(`${API}/onboarding/logo`, {
        logo_type: logoType,
        file_data: fileData,
        file_name: fileName,
        content_type: contentType
      }, { withCredentials: true });
      toast.success('Logo uploaded successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const handleDeleteLogo = async (logoType) => {
    try {
      await axios.delete(`${API}/onboarding/logo/${logoType}`, { withCredentials: true });
      toast.success('Logo deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete logo');
    }
  };

  const handleComplete = async () => {
    try {
      await axios.post(`${API}/onboarding/complete`, {}, { withCredentials: true });
      toast.success('Onboarding completed! Welcome to MediaKshetra GIOS');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to complete onboarding');
    }
  };

  const handleSkip = async () => {
    // Mark as skipped but allow user to proceed
    try {
      await axios.post(`${API}/onboarding/complete`, {}, { withCredentials: true });
      toast.info('You can complete your profile later in Settings');
      navigate('/dashboard');
    } catch (error) {
      navigate('/dashboard');
    }
  };

  const handleNext = async () => {
    if (currentStep === 0 || currentStep === 1) {
      await handleSaveBrief();
    } else if (currentStep === 2) {
      await handleSaveKPI();
    }
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const toggleArrayItem = (array, setArray, field, value) => {
    const current = array[field] || [];
    const newArray = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setArray(prev => ({ ...prev, [field]: newArray }));
  };

  const getLogo = (type) => logos.find(l => l.logo_type === type);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading onboarding...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-8" data-testid="onboarding-page">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">Welcome to MediaKshetra GIOS</h1>
          <p className="text-muted-foreground">Let's set up your account to deliver personalized insights</p>
        </div>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Setup Progress</span>
            <span className="font-medium">{onboardingStatus?.completion_percentage || 0}%</span>
          </div>
          <Progress value={onboardingStatus?.completion_percentage || 0} className="h-2" />
        </div>

        {/* Steps */}
        <StepIndicator currentStep={currentStep} totalSteps={steps.length} steps={steps} />

        {/* Step Content */}
        <Card className="mb-6">
          <CardContent className="p-6">
            {/* Step 1: Company Info */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-semibold">Company Information</h2>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name *</Label>
                    <Input
                      value={brief.company_name}
                      onChange={(e) => setBrief(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="Your Company"
                      data-testid="company-name"
                    />
                  </div>
                  <div>
                    <Label>Industry *</Label>
                    <Select value={brief.industry} onValueChange={(v) => setBrief(prev => ({ ...prev, industry: v }))}>
                      <SelectTrigger data-testid="industry-select">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {options.industries?.map(ind => (
                          <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Company Size</Label>
                    <Select value={brief.company_size} onValueChange={(v) => setBrief(prev => ({ ...prev, company_size: v }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent>
                        {options.company_sizes?.map(size => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={brief.website}
                      onChange={(e) => setBrief(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="https://yourcompany.com"
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-medium mb-4 flex items-center gap-2">
                    <User className="w-4 h-4" /> Primary Contact
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Name *</Label>
                      <Input
                        value={brief.primary_contact_name}
                        onChange={(e) => setBrief(prev => ({ ...prev, primary_contact_name: e.target.value }))}
                        placeholder="John Doe"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={brief.primary_contact_email}
                        onChange={(e) => setBrief(prev => ({ ...prev, primary_contact_email: e.target.value }))}
                        placeholder="john@company.com"
                        data-testid="contact-email"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={brief.primary_contact_phone}
                        onChange={(e) => setBrief(prev => ({ ...prev, primary_contact_phone: e.target.value }))}
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-medium mb-4">Monthly Ad Spend</h3>
                  <Select value={brief.monthly_ad_spend} onValueChange={(v) => setBrief(prev => ({ ...prev, monthly_ad_spend: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select monthly spend range" />
                    </SelectTrigger>
                    <SelectContent>
                      {options.ad_spend_ranges?.map(range => (
                        <SelectItem key={range} value={range}>{range}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 2: Goals & Challenges */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-semibold">Goals & Challenges</h2>
                </div>

                <div>
                  <Label className="mb-3 block">Primary Goals (select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {options.primary_goals?.map(goal => (
                      <label key={goal} className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                        <Checkbox
                          checked={brief.primary_goals?.includes(goal)}
                          onCheckedChange={() => toggleArrayItem(brief, setBrief, 'primary_goals', goal)}
                        />
                        <span className="text-sm">{goal}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="mb-3 block">Main Challenges (select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {options.challenges?.map(challenge => (
                      <label key={challenge} className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                        <Checkbox
                          checked={brief.main_challenges?.includes(challenge)}
                          onCheckedChange={() => toggleArrayItem(brief, setBrief, 'main_challenges', challenge)}
                        />
                        <span className="text-sm">{challenge}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Key Competitors (comma-separated)</Label>
                  <Input
                    value={brief.key_competitors?.join(', ') || ''}
                    onChange={(e) => setBrief(prev => ({ ...prev, key_competitors: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                    placeholder="Competitor A, Competitor B"
                  />
                </div>

                <div>
                  <Label>Target Audience Description</Label>
                  <Textarea
                    value={brief.target_audience}
                    onChange={(e) => setBrief(prev => ({ ...prev, target_audience: e.target.value }))}
                    placeholder="Describe your ideal customer profile, demographics, interests..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Additional Notes</Label>
                  <Textarea
                    value={brief.additional_notes}
                    onChange={(e) => setBrief(prev => ({ ...prev, additional_notes: e.target.value }))}
                    placeholder="Any other information you'd like us to know..."
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Step 3: KPIs & Targets */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-semibold">KPIs & Targets</h2>
                </div>

                <div>
                  <Label className="mb-3 block">Primary KPIs (select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {options.kpis?.map(kpiOption => (
                      <label key={kpiOption} className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                        <Checkbox
                          checked={kpi.primary_kpis?.includes(kpiOption)}
                          onCheckedChange={() => toggleArrayItem(kpi, setKpi, 'primary_kpis', kpiOption)}
                        />
                        <span className="text-sm">{kpiOption}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Target CPA ($)</Label>
                    <Input
                      type="number"
                      value={kpi.target_cpa}
                      onChange={(e) => setKpi(prev => ({ ...prev, target_cpa: e.target.value }))}
                      placeholder="25.00"
                    />
                  </div>
                  <div>
                    <Label>Target ROAS</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={kpi.target_roas}
                      onChange={(e) => setKpi(prev => ({ ...prev, target_roas: e.target.value }))}
                      placeholder="4.0"
                    />
                  </div>
                  <div>
                    <Label>Target CTR (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={kpi.target_ctr}
                      onChange={(e) => setKpi(prev => ({ ...prev, target_ctr: e.target.value }))}
                      placeholder="2.5"
                    />
                  </div>
                  <div>
                    <Label>Monthly Conversion Target</Label>
                    <Input
                      type="number"
                      value={kpi.monthly_conversion_target}
                      onChange={(e) => setKpi(prev => ({ ...prev, monthly_conversion_target: e.target.value }))}
                      placeholder="1000"
                    />
                  </div>
                </div>

                <div>
                  <Label>Reporting Frequency</Label>
                  <Select value={kpi.reporting_frequency} onValueChange={(v) => setKpi(prev => ({ ...prev, reporting_frequency: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      {options.reporting_frequencies?.map(freq => (
                        <SelectItem key={freq} value={freq}>{freq}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Additional Metrics or Goals</Label>
                  <Textarea
                    value={kpi.additional_metrics}
                    onChange={(e) => setKpi(prev => ({ ...prev, additional_metrics: e.target.value }))}
                    placeholder="Any specific metrics or goals not covered above..."
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Step 4: Brand Assets */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 mb-4">
                  <Image className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-semibold">Brand Assets</h2>
                </div>

                <p className="text-sm text-muted-foreground mb-6">
                  Upload your brand logos for a personalized experience. We support PNG, JPEG, SVG, and WebP formats (max 5MB).
                </p>

                <div className="space-y-4">
                  <LogoUploader
                    logoType="primary"
                    label="Primary Logo"
                    description="Your main logo, used in headers and reports"
                    currentLogo={getLogo('primary')}
                    onUpload={handleUploadLogo}
                    onDelete={handleDeleteLogo}
                  />
                  <LogoUploader
                    logoType="secondary"
                    label="Secondary Logo (Optional)"
                    description="Alternative version, e.g., for dark backgrounds"
                    currentLogo={getLogo('secondary')}
                    onUpload={handleUploadLogo}
                    onDelete={handleDeleteLogo}
                  />
                  <LogoUploader
                    logoType="icon"
                    label="Icon/Favicon (Optional)"
                    description="Square icon for compact displays"
                    currentLogo={getLogo('icon')}
                    onUpload={handleUploadLogo}
                    onDelete={handleDeleteLogo}
                  />
                </div>

                {onboardingStatus?.onboarding_complete && (
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="font-medium text-green-400">Onboarding Complete!</p>
                    <p className="text-sm text-muted-foreground">You're all set to use MediaKshetra GIOS</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-muted-foreground"
              data-testid="skip-onboarding"
            >
              <SkipForward className="w-4 h-4 mr-2" /> Skip for now
            </Button>
          </div>

          {currentStep < steps.length - 1 ? (
            <Button onClick={handleNext} disabled={saving} data-testid="next-step">
              {saving ? 'Saving...' : 'Save & Continue'} <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleComplete} 
              className="bg-primary hover:bg-primary/90"
              data-testid="complete-onboarding"
            >
              <CheckCircle className="w-4 h-4 mr-2" /> Complete Setup
            </Button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Onboarding;
